import { Controller } from "@hotwired/stimulus"
import { createApp, reactive } from "@moreonion/petite-vue"
import { withBaseKit, vScopeKit } from "../shared/browserKit"
import { generateUUID } from "../shared/functions"
import {
  petiteDirectiveFormatNumber,
  petiteDirectiveCustomValidations,
  petiteDirectiveInputEffect,
} from "../global/petiteDirectives"
import { compactCurrencyNumber, formatCurrencyNumber } from "../shared/user_methods"
import { downflow, upflow, provideElement, consumeElement } from "../shared/domHelper"
// Connects to :: <section x-ignore v-pre data-controller="petite-vue">
// Script :: <script data-petite-vue-target="setup" >

// data-start-prop-* 속성을 파싱하여 props 객체로 변환
function parseDataProps(element, datasetPrefix = 'prop') {
  const props = {}
  if (!element) return props

  const jsonRegex = new RegExp(`^${datasetPrefix}Json(.)(.+)`)
  const stringRegex = new RegExp(`^${datasetPrefix}String(.)(.+)`)
  const urlRegex = new RegExp(`^${datasetPrefix}Url(.)(.+)`)
  const numberRegex = new RegExp(`^${datasetPrefix}Number(.)(.+)`)
  const booleanRegex = new RegExp(`^${datasetPrefix}Boolean(.)(.+)`)

  Object.keys(element.dataset).forEach(dsKey => {
    if (jsonRegex.test(dsKey)) {
      const key = dsKey.replace(jsonRegex, (_, g1, g2) => g1.toLowerCase() + g2)
      Object.defineProperty(props, key, {
        enumerable: true,
        configurable: false,
        get() {
          try {
            return JSON.parse(element.dataset[dsKey])
          } catch (e) {
            console.error(`${dsKey} parsing error: => `, element.dataset[dsKey], element)
            console.error(e)
            return undefined
          }
        },
      })
    } else if (stringRegex.test(dsKey)) {
      const key = dsKey.replace(stringRegex, (_, g1, g2) => g1.toLowerCase() + g2)
      Object.defineProperty(props, key, {
        enumerable: true,
        configurable: false,
        get() {
          return element.dataset[dsKey]
        },
      })
    } else if (urlRegex.test(dsKey)) {
      const key = dsKey.replace(urlRegex, (_, g1, g2) => g1.toLowerCase() + g2)
      Object.defineProperty(props, key, {
        enumerable: true,
        configurable: false,
        get() {
          return element.dataset[dsKey]
        },
      })
    } else if (numberRegex.test(dsKey)) {
      const key = dsKey.replace(numberRegex, (_, g1, g2) => g1.toLowerCase() + g2)
      Object.defineProperty(props, key, {
        enumerable: true,
        configurable: false,
        get() {
          return Number(element.dataset[dsKey])
        },
      })
    } else if (booleanRegex.test(dsKey)) {
      const key = dsKey.replace(booleanRegex, (_, g1, g2) => g1.toLowerCase() + g2)
      Object.defineProperty(props, key, {
        enumerable: true,
        configurable: false,
        get() {
          const value = element.dataset[dsKey]
          return !(value.trim() === '' || value === 'false' || value === '0' || value === 'null')
        },
      })
    }
  })
  return props
}

// 상위 컨트롤러 connect
// 상위가 먼저 connect됨
// 하위 petite-vue 엘리먼트들을 찾아서 vueWaiters에 등록
// 하위들이 준비될 때까지 대기

// 하위 컨트롤러 connect
// 자신의 vueWaiters에 parent 정보가 이미 등록되어 있음
// 상위가 대기 중이므로 하위 Vue 초기화를 완료 해야 함

export default class extends Controller {

  // setup target은 optional이므로 static targets에 등록하지 않고 직접 쿼리
  // 중첩된 컨트롤러의 setup은 제외하고 직계 자식만 검색
  get setupTarget() {
    const allSetupScripts = Array.from(
      this.element.querySelectorAll('[data-petite-vue-target="setup"]')
    )

    // 중첩된 petite-vue 컨트롤러 내부의 setup은 제외
    return allSetupScripts.find(script => {
      // script의 부모 중에서 가장 가까운 petite-vue 컨트롤러를 찾음
      const closestController = script.closest('[data-controller~="petite-vue"]')
      // 현재 컨트롤러와 일치하는 경우만 반환
      return closestController === this.element
    }) || null
  }

  static values = {
    noscript: {
      type: Boolean,
      default: false,
    },
    script: {
      type: String,
      default: "",
    },
    props: {
      type: Object,
      default: {},
    },
  }

  startVueApp(appConfig) {
    const rootElement = this.element
    const baseKits = withBaseKit()

    // app root default
    let app = createApp(
      Object.defineProperties(
        appConfig, {
        $root: {
          enumerable: false,
          configurable: false,
          get() {
            return rootElement
          }
        },
        $scopeKits: {
          enumerable: false,
          configurable: false,
          value: function scopeKits(callback) {
            const instance = vScopeKit((kits) => callback({ ...kits }))
            console.log("scope instantce", instance)
            return instance
          }
        },
        $reactive: {
          enumerable: false,
          configurable: false,
          value: function vReactive(object) {
            return reactive(object)
          }
        },
        // deprecated - 사용처가 하나밖에 없고 일반적으로 필요한 시나리오가 아님
        $parentTick: {
          enumerable: false,
          configurable: false,
          value: function (callback) {
            // rootElement.vueWaiters에서 parent 정보를 찾음
            if (rootElement.vueWaiters && rootElement.vueWaiters.length > 0) {
              // 현재 element의 vueWaiters 중에서 parent 정보가 있는 waiter를 찾음
              const parentWaiter = rootElement.vueWaiters.find(waiter => waiter.parent)

              if (parentWaiter && parentWaiter.parent) {
                const parentVueController = parentWaiter.parent

                if (parentVueController.vueApp) {
                  // 상위 vue가 이미 초기화되어 있으면 즉시 실행
                  if (typeof callback === 'function') {
                    callback()
                  }
                } else {
                  // 상위 vue가 아직 초기화되지 않은 경우 상위의 vueWaiters에 추가
                  if (!parentVueController.vueWaiters) {
                    parentVueController.vueWaiters = []
                  }

                  let resolve, reject
                  const readyPromise = new Promise((res, rej) => {
                    resolve = res
                    reject = rej
                  })

                  parentVueController.vueWaiters.push({
                    type: 'parentTick',
                    element: rootElement,
                    callback: callback,
                    promise: readyPromise,
                    resolve: () => {
                      resolve()
                      if (typeof callback === 'function') {
                        callback()
                      }
                    },
                    reject
                  })
                }
              } else {
                // parent 정보가 없으면 즉시 실행
                if (typeof callback === 'function') {
                  callback()
                }
              }
            } else {
              // vueWaiters가 없으면 즉시 실행
              if (typeof callback === 'function') {
                callback()
              }
            }
          }
        },
        $qss: {
          enumerable: false,
          configurable: false,
          value: function qsStringify(object, options = {}) {
            const allowEmptyArrays = options?.allowEmptyArrays ?? true
            const arrayFormat = options?.arrayFormat ?? 'brackets'
            return baseKits.queryStringify(object, {
              arrayFormat,
              allowEmptyArrays,
            })
          },
        },
        $qsp: {
          enumerable: false,
          configurable: false,
          get() {
            return baseKits.queryStringParse
          }
        },
        $qsa: {
          enumerable: false,
          configurable: false,
          value: function (url, queryData) {
            const qsData = baseKits.reflect(queryData)
            return baseKits.queryStringAssign(url, qsData)
          }
        },
        $reflect: {
          enumerable: false,
          configurable: false,
          value: function reflect(value) {
            return baseKits.reflect(value)
          }
        },
        // deprecated
        $taggets: {
          enumerable: false,
          configurable: false,
          value: function selectTargets(targetName, mapCallback) {
            return baseKits.selectElements([this.$root, `[tagget="${targetName}"]`], mapCallback)
          },
        },
        // deprecated
        $tagget: {
          enumerable: false,
          configurable: false,
          value: function selectTarget(targetName, mapCallback) {
            return baseKits.selectElement([this.$root, `[tagget="${targetName}"]`], mapCallback)
          },
        },
        $selects: {
          enumerable: false,
          configurable: false,
          value: function vSelectElements(selector, mapCallback) {
            return baseKits.selectElements(selector, mapCallback)
          }
        },
        $select: {
          enumerable: false,
          configurable: false,
          value: function vSelectElement(selector, mapCallback) {
            return baseKits.selectElement(selector, mapCallback)
          }
        },
        $dispatch: {
          enumerable: false,
          configurable: false,
          value: function vDispatchElement(selector, eventName, detail, bubbles = true) {
            baseKits.dispatchElement(selector, eventName, detail, bubbles)
          }
        },
        $handle: {
          enumerable: false,
          configurable: false,
          value: function vDispatchElement(selector, eventName, detail) {
            baseKits.handleElement(selector, eventName, detail)
          }
        },
        $emit: {
          enumerable: false,
          configurable: false,
          value: function vEmit(eventName, detail, bubbles = true) {
            return baseKits.dispatchElement(rootElement, eventName, detail, bubbles)
          }
        },
        $emitCallback: {
          enumerable: false,
          configurable: false,
          value: async function vEmitRecall(eventName, callbackName, handler, bubbles = true) {
            const promise = new Promise((resolve) => {
              baseKits.dispatchElement(
                rootElement,
                eventName,
                {
                  [callbackName]: (...args) => {
                    const result = handler(...args)
                    resolve(result)
                  }
                },
                bubbles
              )
            })
            return promise
          }
        },
        $doit: {
          enumerable: false,
          configurable: false,
          get() {
            return baseKits.doit
          }
        },
        $format: {
          enumerable: false,
          configurable: false,
          value: function format(type, value) {
            if (type === 'currency' || type === ',000') {
              return formatCurrencyNumber(value)
            }
            if (type === 'compactCurrency') {
              const currencyValue = compactCurrencyNumber(value)
              return currencyValue
            }
            return value
          }
        },
        $kst: {
          enumerable: false,
          configurable: false,
          value: baseKits.kst,
        },
        // deprecated: provideElement/consumeElement로 대체 권장
        $downflow: {
          enumerable: false,
          configurable: false,
          value: (element, flowData, matcher) => {
            return downflow(element, flowData, matcher)
          }
        },
        // @vue:mounted="upflow($el, (data)=>{})" 형태로 사용
        // deprecated
        $upflow: {
          enumerable: false,
          configurable: false,
          value: (element, handleFlow, matcher = null) => {
            return upflow(element, handleFlow, matcher)
          }
        },
        // provideElement: DOM 트리 기반 Provider-Consumer 패턴 (downflow/upflow 개선판)
        // @vue:mounted="$provide($el, 'scope-name', (provider)=>({ connected(consumer){ return context } }))" 형태로 사용
        $provide: {
          enumerable: false,
          configurable: false,
          value: (element, matchers, initializeCallback = null) => {
            return provideElement(element, matchers, initializeCallback)
          }
        },
        // consumeElement: DOM 트리 기반 Provider-Consumer 패턴 (downflow/upflow 개선판)
        // @vue:mounted="$consume($el, 'scope-name', (consumer)=>({ enter(context){}, exit(context, error){} }))" 형태로 사용
        $consume: {
          enumerable: false,
          configurable: false,
          value: (element, assert, initializeCallback = null) => {
            return consumeElement(element, assert, initializeCallback)
          }
        },
      })
    )

    // directive
    app = petiteDirectiveFormatNumber(app)
    app = petiteDirectiveCustomValidations(app)
    app = petiteDirectiveInputEffect(app)

    // mount
    rootElement.vueApp = app

    if (typeof appConfig?.init === "function") {
      appConfig.init.call(app)
      app.mount(rootElement)
    } else {
      app.mount(rootElement)
    }

    // 하위에 vue 가 있으면 기다리도록 함
    if (rootElement.vueWaiters) {
      rootElement.vueWaiters.forEach((waiter) => {
        if (typeof waiter.resolve === 'function') {
          waiter.resolve()
        }
      })
      // waiter들이 모두 처리되었으므로 배열 초기화
      rootElement.vueWaiters = []
    }
    // console.log("PetiteVue mounted", rootElement)
    return app
  }

  connect() {
    const controller = this
    const rootElement = this.element
    const targetScript = this.setupTarget
    const noscriptMode = this.noscriptValue
    const inlineScript = this.scriptValue
    const setupUUID = generateUUID("petite_vue_xxxx_xxxx_yxxx")
    let setupExecuted = false
    function setupPetiteVue(appConfig) {
      if (setupExecuted) {
        throw new Error("petite-vue의 셋업 스크립트는 한 번만 호출되어야 합니다.")
      } else {
        setupExecuted = true
      }
      if (rootElement.vueApp) {
        console.errer("PetiteVue is already mounted", rootElement)
        return null
      } else {
        // 하위 petite-vue 컨트롤러 엘리먼트들을 찾고, 이미 vueApp이 없는 것만 필터링
        const childPetiteVueElements = Array.from(
          rootElement.querySelectorAll('[data-controller~="petite-vue"]')
        ).filter(element => !element.vueApp)

        if (childPetiteVueElements.length) {
          // 하위 뷰가 감지되면 하위 뷰가 준비될 때까지 대기
          const childReadyPromises = []

          childPetiteVueElements.forEach(element => {
            if (!element.vueWaiters) {
              element.vueWaiters = []
            }
            let resolve, reject
            const readyPromise = new Promise((res, rej) => {
              resolve = res
              reject = rej
            })
            childReadyPromises.push(readyPromise)
            element.vueWaiters.push({
              type: 'childInit',
              element: element,
              parent: rootElement,
              promise: readyPromise,
              resolve,
              reject,
            })
          })
          Promise.all(childReadyPromises).then(() => {
            controller.startVueApp(appConfig)
          })
        } else {
          controller.startVueApp(appConfig)
        }
      }
    }

    if (noscriptMode === true) {
      // noScript 모드: setup 스크립트 없이 v-scope만 사용
      if (targetScript) {
        console.warn(
          "🚨 petite-vue noScript 모드 경고:\n" +
          "현재 data-petite-vue-noscript-value=\"true\" 모드입니다.\n" +
          "이 모드에서는 <script data-petite-vue-target=\"setup\"> 태그가 실행되지 않습니다.\n" +
          "v-scope 속성만 사용하여 반응형 데이터를 작성해야 합니다.\n\n" +
          "<script data-petite-vue-target=\"setup\"> 초기화가 필요하다면 data-petite-vue-noscript-value=\"true\"를 제거하세요.",
          { rootElement, targetScript }
        )
      }
      // noScript 모드에서도 Vue 앱을 마운트해야 함 (v-scope 처리 + 부모-자식 대기 로직)
      setupPetiteVue({})
    } else if (inlineScript) {
      // data-petite-vue-script-value 모드: 인라인 스크립트 실행
      if (targetScript) {
        console.warn(
          "🚨 petite-vue 인라인 스크립트 모드 경고:\n" +
          "현재 data-petite-vue-script-value가 설정되어 있습니다.\n" +
          "<script data-petite-vue-target=\"setup\"> 태그는 무시됩니다.\n" +
          "인라인 스크립트만 실행됩니다.",
          { rootElement, targetScript }
        )
      }

      // data-petite-vue-props-value를 props로 제공 (Stimulus가 자동 파싱)
      const props = this.propsValue

      // start 함수 정의
      function start(setupCallback) {
        const result = setupPetiteVue(vScopeKit((kits) => setupCallback({ ...kits })))
        return result
      }

      try {
        // new Function으로 인라인 스크립트 실행
        const scriptFunction = new Function('start', 'props', inlineScript)
        scriptFunction(start, props)
      } catch (error) {
        // 상세한 에러 정보 출력
        console.group("🚨 data-petite-vue-script-value 파싱 오류")
        if (error.stack) {
          console.error("Stack Trace:", error.stack)
        } else {
          console.error("Error:", error.message)
        }
        console.log("📄 Script Content:")
        console.log(inlineScript)
        console.log("📦 Props:", props)
        console.log("🎯 Element:", rootElement)
        console.groupEnd()
        // 오류 발생 시에도 빈 Vue 앱 마운트
        setupPetiteVue({})
      }
    } else if (targetScript) {
      // data-petite-vue-props-value와 script의 data-start-prop-*을 병합
      const controllerProps = this.propsValue
      const scriptProps = parseDataProps(targetScript, 'startProp')
      const props = { ...controllerProps, ...scriptProps }

      function jumpStarter(setupCallback) {
        const result = setupPetiteVue(vScopeKit((kits) => setupCallback({ ...kits, props })))
        if (result && window[setupUUID]) {
          delete window[setupUUID]
        }
        return result
      }
      // jumpStarter를 호출할 때 props도 함께 전달하는 래퍼
      function jumpStarterWithProps(setupCallback) {
        return jumpStarter(setupCallback)
      }
      // xSetupStart의 callback에 (start, props) 형태로 전달할 수 있도록 props 첨부
      jumpStarterWithProps.props = props

      if (targetScript.dataset.connectPeerId) {
        // stimulus가 script보다 먼저 준비된 경우 - 페이지 열기, Turbo.visit
        window[targetScript.dataset.connectPeerId](jumpStarter, props)
      } else {
        // script가 stimulus보다 먼저 실행된 경우 - turbo-frame 로드시
        // xSetupStart에서 jumpStarter(callback) 호출 시 props를 같이 전달
        targetScript.dataset.connectHostId = setupUUID
        window[setupUUID] = function (callback) {
          callback(jumpStarter, props)
        }
      }
    } else {
      console.warn("petite-vue의 셋업 스크립트가 없습니다.\n <script data-petite-vue-target=\"setup\">xSetupStart(...)</script> 태그를 사용을 권장합니다.")
      setupPetiteVue({})
    }
  }
}

